
import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import ASSETS from '../../assets';
import Typography from '../../components/Typography/Typography';
import Profile from '../../dialogs/Profile/Profile';
import Navigation from '../../components/Navigation/Navigation';
import UserProfileDropdown from '../../components/UserProfileDropdown/UserProfileDropdown';
import { RootState } from '../../store';

const DashboardLayout = () => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, profileLoading } = useSelector((state: RootState) => state.auth);

  // Fetch user profile when component mounts
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const result = await dispatch.auth.fetchUserProfileAsync();
        if (!result.success) {
          toast.error(result.message || 'Failed to load user profile');
        }
      } catch (error) {
        toast.error('An unexpected error occurred while loading profile');
      }
    };

    fetchProfile();
  }, [dispatch]);

  // Handle click outside to close mobile menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  const handleProfileClick = () => {
    setIsProfileOpen(true);
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLogout = async () => {
    try {
      await dispatch.auth.logoutAsync();
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Error during logout');
    }
  };


  

  return (
    <div className='min-h-screen bg-white flex flex-col'>
      {/* Header */}
      <div className='h-[60px] sm:h-[70px] lg:h-[80px] bg-g_gradient'>
        <div className='h-full flex items-center justify-center gap-2 sm:gap-5 bg-f_gradient px-3 sm:px-8 lg:px-20'>
          <div className='w-full px-1 sm:px-5 flex items-center justify-between'>
            {/* Left Side - Logo Section */}
            <div className='flex items-center gap-1 sm:gap-3 lg:gap-5 cursor-pointer' onClick={() => navigate('/dashboard')}>
              <img 
                src={ASSETS.kpmg_logo} 
                alt='KPMG Logo' 
                className='w-12 h-12 sm:w-20 sm:h-20 lg:w-28 lg:h-28'
              />
              <Typography 
                variant='h3' 
                color='white'
                className='text-sm sm:text-xl lg:text-2xl'
              >
                +
              </Typography>
              <img 
                src={ASSETS.kresus_logo} 
                alt='Kresus Logo' 
                className='w-12 h-12 sm:w-20 sm:h-20 lg:w-28 lg:h-28'
              />
            </div>
            
            {/* Right Side - Navigation + User Profile */}
            <div className='hidden md:flex items-center gap-4 sm:gap-6 lg:gap-8'>
              {/* Navigation Component */}
              <Navigation />
              
              {/* User Profile Dropdown */}
              <UserProfileDropdown
                userName={user ? `${user.firstName} ${user.lastName}` : 'Loading...'}
                company="KPMG"
                onProfileClick={handleProfileClick}
                onLogout={handleLogout}
              />
            </div>
            
            {/* Mobile User Profile - Visible only on mobile */}
            <div ref={mobileMenuRef} className='md:hidden flex items-center relative'>
              <button
                onClick={handleMobileMenuToggle}
                className='flex items-center gap-2 bg-white/10 hover:bg-white/20 rounded-lg px-2 py-1 transition-colors duration-200'
              >
                <div className='w-6 h-6 bg-white/20 rounded-full flex items-center justify-center'>
                  <span className='text-white text-xs font-semibold'>
                    {user ? `${user.firstName?.charAt(0)}${user.lastName?.charAt(0)}` : 'U'}
                  </span>
                </div>
                <span className='text-white text-xs font-medium hidden xs:inline'>
                  {user ? `${user.firstName} ${user.lastName}` : 'Loading...'}
                </span>
                <svg 
                  className={`w-3 h-3 text-white transition-transform duration-200 ${isMobileMenuOpen ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Mobile Dropdown Menu */}
              {isMobileMenuOpen && (
                <div className='absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50'>
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleProfileClick();
                    }}
                    className='w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2'
                  >
                    <svg className='w-4 h-4' fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Profile
                  </button>
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleLogout();
                    }}
                    className='w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2'
                  >
                    <svg className='w-4 h-4' fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Navigation - Visible only on mobile */}
      <div className='md:hidden bg-g_gradient border-t border-white/20'>
        <div className='px-3 py-2'>
          <Navigation />
        </div>
      </div>
      
      {/* Main Content Area */}
      <div className='h-[100vh] flex flex-col min-h-[calc(100vh-60px)] mb-4 overflow-y-auto w-full bg-white px-4 sm:px-8 lg:px-20 py-3 sm:py-3 lg:py-3'>
        <Outlet />
      </div>
      
      {/* Profile Modal */}
      <Profile 
        open={isProfileOpen} 
        setOpen={setIsProfileOpen} 
      />
    </div>
  );
};

export default DashboardLayout;
